// Brevo API configuration
const BREVO_API_URL = 'https://api.brevo.com/v3/smtp/email';
const BREVO_API_KEY = process.env.REACT_APP_BREVO_API_KEY;

// Email service class
class EmailService {
  constructor() {
    this.fromEmail = process.env.REACT_APP_BREVO_SENDER_EMAIL;
    this.fromName = process.env.REACT_APP_BREVO_SENDER_NAME;
  }

  // Generic method to send email via Brevo
  async sendEmail(to, subject, htmlContent, textContent = '') {
    try {
      const emailData = {
        sender: {
          name: this.fromName,
          email: this.fromEmail
        },
        to: [
          {
            email: to.email,
            name: to.name || 'User'
          }
        ],
        subject: subject,
        htmlContent: htmlContent,
        textContent: textContent || this.stripHtml(htmlContent)
      };

      const response = await fetch(BREVO_API_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'api-key': BREVO_API_KEY
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Brevo API error: ${error.message || response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Email sent successfully:', result);
      
      return { success: true, messageId: result.messageId };
    } catch (error) {
      console.error('❌ Error sending email:', error);
      throw error;
    }
  }

  // Utility method to strip HTML tags for text content
  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  // EMAIL TEMPLATES WITH OODLES BRANDING

  // 1. Employer Application Accepted
  async sendEmployerAcceptedEmail(employerData) {
    const subject = '🎉 Welcome to Oodles Internships - Your Account is Now Active!';
    const htmlContent = this.getEmployerAcceptedTemplate(employerData);
    
    return await this.sendEmail(
      { email: employerData.email, name: employerData.companyName },
      subject,
      htmlContent
    );
  }

  // 2. Employer Application Rejected
  async sendEmployerRejectedEmail(employerData, reason = '') {
    const subject = 'Update on Your Oodles Internships Application';
    const htmlContent = this.getEmployerRejectedTemplate(employerData, reason);
    
    return await this.sendEmail(
      { email: employerData.email, name: employerData.companyName },
      subject,
      htmlContent
    );
  }

  // 3. Student Application Accepted by Employer
  async sendStudentApplicationAcceptedEmail(studentData, positionData, companyData) {
    const subject = '🎉 Congratulations! Your Application Has Been Accepted';
    const htmlContent = this.getStudentAcceptedTemplate(studentData, positionData, companyData);
    
    return await this.sendEmail(
      { email: studentData.email, name: studentData.name },
      subject,
      htmlContent
    );
  }

  // 4. Student Application Rejected by Employer
  async sendStudentApplicationRejectedEmail(studentData, positionData, companyData) {
    const subject = 'Update on Your Internship Application';
    const htmlContent = this.getStudentRejectedTemplate(studentData, positionData, companyData);
    
    return await this.sendEmail(
      { email: studentData.email, name: studentData.name },
      subject,
      htmlContent
    );
  }

  // 5. Student Accepts Offer
  async sendStudentAcceptedOfferEmail(employerData, studentData, positionData) {
    const subject = '✅ Student Has Accepted Your Internship Offer';
    const htmlContent = this.getStudentAcceptedOfferTemplate(employerData, studentData, positionData);
    
    return await this.sendEmail(
      { email: employerData.email, name: employerData.companyName },
      subject,
      htmlContent
    );
  }

  // 6. Student Declines Offer
  async sendStudentDeclinedOfferEmail(employerData, studentData, positionData, reason = '') {
    const subject = 'Student Response: Internship Offer Declined';
    const htmlContent = this.getStudentDeclinedOfferTemplate(employerData, studentData, positionData, reason);
    
    return await this.sendEmail(
      { email: employerData.email, name: employerData.companyName },
      subject,
      htmlContent
    );
  }

  // TEMPLATE METHODS

  getEmployerAcceptedTemplate(employerData) {
    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Welcome to Oodles Internships</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f9fafb;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #fb923c 0%, #f97316 100%); padding: 30px 20px; text-align: center;">
                <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">
                    🎉 Welcome to Oodles Internships!
                </h1>
                <p style="color: #fed7aa; margin: 10px 0 0; font-size: 16px;">
                    Your employer account has been approved
                </p>
            </div>

            <!-- Content -->
            <div style="padding: 40px 30px;">
                <p style="font-size: 18px; color: #1f2937; margin: 0 0 20px; font-weight: 600;">
                    Dear ${employerData.companyName} Team,
                </p>
                
                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 0 0 20px;">
                    Congratulations! We're excited to inform you that your employer application has been <strong style="color: #059669;">approved and verified</strong>. Your account is now active and you can start posting internship opportunities.
                </p>

                <div style="background-color: #d1fae5; border-left: 4px solid #10b981; padding: 20px; margin: 30px 0; border-radius: 6px;">
                    <h3 style="color: #065f46; margin: 0 0 10px; font-size: 18px;">What's Next?</h3>
                    <ul style="color: #047857; margin: 0; padding-left: 20px; line-height: 1.6;">
                        <li>Complete your company profile</li>
                        <li>Post your first internship position</li>
                        <li>Start reviewing student applications</li>
                        <li>Access our employer dashboard and analytics</li>
                    </ul>
                </div>

                <div style="text-align: center; margin: 30px 0;">
                    <a href="https://pilot.oodlesinternships.com/employer/login" 
                       style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); 
                              color: #ffffff; 
                              text-decoration: none; 
                              padding: 15px 30px; 
                              border-radius: 8px; 
                              font-weight: 600; 
                              font-size: 16px; 
                              display: inline-block;
                              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                        Access Your Dashboard →
                    </a>
                </div>

                <p style="font-size: 14px; color: #6b7280; line-height: 1.6; margin: 30px 0 0;">
                    If you have any questions or need assistance, please don't hesitate to reach out to our support team.
                </p>
            </div>

            <!-- Footer -->
            <div style="background-color: #f3f4f6; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0; font-size: 14px; color: #6b7280;">
                    Best regards,<br>
                    <strong style="color: #f97316;">Oodles Internships Team</strong>
                </p>
                <p style="margin: 10px 0 0; font-size: 12px; color: #9ca3af;">
                    Shaping the future of Internships
                </p>
            </div>
        </div>
    </body>
    </html>`;
  }

  getEmployerRejectedTemplate(employerData, reason) {
    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Application Update - Oodles Internships</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f9fafb;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #fb923c 0%, #f97316 100%); padding: 30px 20px; text-align: center;">
                <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">
                    Oodles Internships
                </h1>
                <p style="color: #fed7aa; margin: 10px 0 0; font-size: 16px;">
                    Application Update
                </p>
            </div>

            <!-- Content -->
            <div style="padding: 40px 30px;">
                <p style="font-size: 18px; color: #1f2937; margin: 0 0 20px; font-weight: 600;">
                    Dear ${employerData.companyName} Team,
                </p>
                
                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 0 0 20px;">
                    Thank you for your interest in joining the Oodles Internships platform. After careful review of your application, we regret to inform you that we cannot approve your employer account at this time.
                </p>

                ${reason ? `
                <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 20px; margin: 30px 0; border-radius: 6px;">
                    <h3 style="color: #92400e; margin: 0 0 10px; font-size: 16px;">Reason:</h3>
                    <p style="color: #92400e; margin: 0; line-height: 1.6;">${reason}</p>
                </div>
                ` : ''}

                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 20px 0;">
                    If you believe this decision was made in error or if you would like to address any concerns, please feel free to contact our support team. We appreciate your understanding.
                </p>

                <div style="text-align: center; margin: 30px 0;">
                    <a href="mailto:support@oodlesinternships.com" 
                       style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); 
                              color: #ffffff; 
                              text-decoration: none; 
                              padding: 15px 30px; 
                              border-radius: 8px; 
                              font-weight: 600; 
                              font-size: 16px; 
                              display: inline-block;
                              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                        Contact Support
                    </a>
                </div>
            </div>

            <!-- Footer -->
            <div style="background-color: #f3f4f6; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0; font-size: 14px; color: #6b7280;">
                    Best regards,<br>
                    <strong style="color: #f97316;">Oodles Internships Team</strong>
                </p>
            </div>
        </div>
    </body>
    </html>`;
  }

  getStudentAcceptedTemplate(studentData, positionData, companyData) {
    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Application Accepted - Oodles Internships</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f9fafb;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #fb923c 0%, #f97316 100%); padding: 30px 20px; text-align: center;">
                <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">
                    🎉 Congratulations!
                </h1>
                <p style="color: #fed7aa; margin: 10px 0 0; font-size: 16px;">
                    Your application has been accepted
                </p>
            </div>

            <!-- Content -->
            <div style="padding: 40px 30px;">
                <p style="font-size: 18px; color: #1f2937; margin: 0 0 20px; font-weight: 600;">
                    Dear ${studentData.name},
                </p>
                
                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 0 0 20px;">
                    Excellent news! <strong style="color: #059669;">${companyData.companyName}</strong> has accepted your application for the <strong>${positionData.title}</strong> position.
                </p>

                <div style="background-color: #d1fae5; border-left: 4px solid #10b981; padding: 20px; margin: 30px 0; border-radius: 6px;">
                    <h3 style="color: #065f46; margin: 0 0 15px; font-size: 18px;">⏰ IMPORTANT: Response Required</h3>
                    <p style="color: #047857; margin: 0; line-height: 1.6; font-weight: 600;">
                        You have <strong>14 days</strong> to respond to this offer. Please log in to your dashboard to accept or decline this opportunity.
                    </p>
                </div>

                <div style="text-align: center; margin: 30px 0;">
                    <a href="${window.location.origin}/applications" 
                       style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); 
                              color: #ffffff; 
                              text-decoration: none; 
                              padding: 15px 30px; 
                              border-radius: 8px; 
                              font-weight: 600; 
                              font-size: 16px; 
                              display: inline-block;
                              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                        Respond to Offer →
                    </a>
                </div>

                <p style="font-size: 14px; color: #dc2626; line-height: 1.6; margin: 20px 0; text-align: center; font-weight: 600;">
                    ⚠️ If you don't respond within 14 days, the offer will automatically expire.
                </p>
            </div>

            <!-- Footer -->
            <div style="background-color: #f3f4f6; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0; font-size: 14px; color: #6b7280;">
                    Best regards,<br>
                    <strong style="color: #f97316;">Oodles Internships Team</strong>
                </p>
            </div>
        </div>
    </body>
    </html>`;
  }

  getStudentRejectedTemplate(studentData, positionData, companyData) {
    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Application Update - Oodles Internships</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f9fafb;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #fb923c 0%, #f97316 100%); padding: 30px 20px; text-align: center;">
                <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">
                    Oodles Internships
                </h1>
                <p style="color: #fed7aa; margin: 10px 0 0; font-size: 16px;">
                    Application Update
                </p>
            </div>

            <!-- Content -->
            <div style="padding: 40px 30px;">
                <p style="font-size: 18px; color: #1f2937; margin: 0 0 20px; font-weight: 600;">
                    Dear ${studentData.name},
                </p>
                
                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 0 0 20px;">
                    Thank you for your interest in the <strong>${positionData.title}</strong> position at <strong>${companyData.companyName}</strong>. After careful consideration, they have decided to move forward with other candidates for this role.
                </p>

                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 0 0 20px;">
                    While this particular opportunity didn't work out, we encourage you to keep applying to other positions that match your skills and interests.
                </p>

                <div style="background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 20px; margin: 30px 0; border-radius: 6px;">
                    <h3 style="color: #1e40af; margin: 0 0 10px; font-size: 16px;">💪 Keep Going!</h3>
                    <p style="color: #1e40af; margin: 0; line-height: 1.6;">
                        Don't let this discourage you. Many successful professionals faced rejections before finding their perfect opportunity. Keep building your skills and applying!
                    </p>
                </div>

                <div style="text-align: center; margin: 30px 0;">
                    <a href="${window.location.origin}/explore" 
                       style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); 
                              color: #ffffff; 
                              text-decoration: none; 
                              padding: 15px 30px; 
                              border-radius: 8px; 
                              font-weight: 600; 
                              font-size: 16px; 
                              display: inline-block;
                              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                        Explore More Opportunities →
                    </a>
                </div>
            </div>

            <!-- Footer -->
            <div style="background-color: #f3f4f6; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0; font-size: 14px; color: #6b7280;">
                    Best regards,<br>
                    <strong style="color: #f97316;">Oodles Internships Team</strong>
                </p>
            </div>
        </div>
    </body>
    </html>`;
  }

  getStudentAcceptedOfferTemplate(employerData, studentData, positionData) {
    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Student Accepted Your Offer - Oodles Internships</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f9fafb;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #fb923c 0%, #f97316 100%); padding: 30px 20px; text-align: center;">
                <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">
                    ✅ Great News!
                </h1>
                <p style="color: #fed7aa; margin: 10px 0 0; font-size: 16px;">
                    Your offer has been accepted
                </p>
            </div>

            <!-- Content -->
            <div style="padding: 40px 30px;">
                <p style="font-size: 18px; color: #1f2937; margin: 0 0 20px; font-weight: 600;">
                    Hello,
                </p>
                
                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 0 0 20px;">
                    Excellent news! <strong style="color: #059669;">${studentData.name}</strong> has accepted your internship offer for the <strong>${positionData.title}</strong> position.
                </p>

                <div style="background-color: #f8fafc; padding: 20px; margin: 30px 0; border-radius: 8px; border: 1px solid #e2e8f0;">
                    <h3 style="color: #1e293b; margin: 0 0 15px; font-size: 16px;">👤 Student Details:</h3>
                    <ul style="color: #475569; margin: 0; padding-left: 20px; line-height: 1.8;">
                        <li><strong>Name:</strong> ${studentData.name}</li>
                        <li><strong>Email:</strong> ${studentData.email}</li>
                        <li><strong>Position:</strong> ${positionData.title}</li>
                    </ul>
                </div>

                <div style="background-color: #d1fae5; border-left: 4px solid #10b981; padding: 20px; margin: 30px 0; border-radius: 6px;">
                    <h3 style="color: #065f46; margin: 0 0 10px; font-size: 16px;">📋 Next Steps:</h3>
                    <ul style="color: #047857; margin: 0; padding-left: 20px; line-height: 1.6;">
                        <li>Contact the student to discuss start date and onboarding</li>
                        <li>Send any required documentation or contracts</li>
                        <li>Prepare orientation materials</li>
                        <li>Update your hiring records</li>
                    </ul>
                </div>

                <div style="text-align: center; margin: 30px 0;">
                    <a href="${window.location.origin}/employer/applications" 
                       style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); 
                              color: #ffffff; 
                              text-decoration: none; 
                              padding: 15px 30px; 
                              border-radius: 8px; 
                              font-weight: 600; 
                              font-size: 16px; 
                              display: inline-block;
                              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                        View Application Details →
                    </a>
                </div>
            </div>

            <!-- Footer -->
            <div style="background-color: #f3f4f6; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0; font-size: 14px; color: #6b7280;">
                    Best regards,<br>
                    <strong style="color: #f97316;">Oodles Internships Team</strong>
                </p>
            </div>
        </div>
    </body>
    </html>`;
  }

  getStudentDeclinedOfferTemplate(employerData, studentData, positionData, reason) {
    return `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Student Declined Your Offer - Oodles Internships</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f9fafb;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #fb923c 0%, #f97316 100%); padding: 30px 20px; text-align: center;">
                <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">
                    Oodles Internships
                </h1>
                <p style="color: #fed7aa; margin: 10px 0 0; font-size: 16px;">
                    Student Response Update
                </p>
            </div>

            <!-- Content -->
            <div style="padding: 40px 30px;">
                <p style="font-size: 18px; color: #1f2937; margin: 0 0 20px; font-weight: 600;">
                    Hello,
                </p>
                
                <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin: 0 0 20px;">
                    We wanted to inform you that <strong>${studentData.name}</strong> has declined your internship offer for the <strong>${positionData.title}</strong> position.
                </p>

                <div style="background-color: #f8fafc; padding: 20px; margin: 30px 0; border-radius: 8px; border: 1px solid #e2e8f0;">
                    <h3 style="color: #1e293b; margin: 0 0 15px; font-size: 16px;">📋 Application Details:</h3>
                    <ul style="color: #475569; margin: 0; padding-left: 20px; line-height: 1.8;">
                        <li><strong>Student:</strong> ${studentData.name}</li>
                        <li><strong>Position:</strong> ${positionData.title}</li>
                    </ul>
                </div>

                ${reason ? `
                <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 20px; margin: 30px 0; border-radius: 6px;">
                    <h3 style="color: #92400e; margin: 0 0 10px; font-size: 16px;">📝 Student's Reason:</h3>
                    <p style="color: #92400e; margin: 0; line-height: 1.6; font-style: italic;">"${reason}"</p>
                </div>
                ` : ''}

                <div style="background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 20px; margin: 30px 0; border-radius: 6px;">
                    <h3 style="color: #1e40af; margin: 0 0 10px; font-size: 16px;">💡 Suggestions:</h3>
                    <ul style="color: #1e40af; margin: 0; padding-left: 20px; line-height: 1.6;">
                        <li>Review other qualified candidates for this position</li>
                        <li>Consider reaching out to candidates in your interview pool</li>
                        <li>Post the position again if needed</li>
                        <li>Collect feedback to improve future offers</li>
                    </ul>
                </div>

                <div style="text-align: center; margin: 30px 0;">
                    <a href="${window.location.origin}/employer/applications" 
                       style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); 
                              color: #ffffff; 
                              text-decoration: none; 
                              padding: 15px 30px; 
                              border-radius: 8px; 
                              font-weight: 600; 
                              font-size: 16px; 
                              display: inline-block;
                              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                        View Other Candidates →
                    </a>
                </div>
            </div>

            <!-- Footer -->
            <div style="background-color: #f3f4f6; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0; font-size: 14px; color: #6b7280;">
                    Best regards,<br>
                    <strong style="color: #f97316;">Oodles Internships Team</strong>
                </p>
            </div>
        </div>
    </body>
    </html>`;
  }
}

// Export singleton instance
export const emailService = new EmailService();

// Utility function to send notifications (use this in your components)
export const sendNotificationEmail = async (type, data) => {
  try {
    console.log(`📧 Sending ${type} notification email...`, data);
    
    switch (type) {
      case 'EMPLOYER_ACCEPTED':
        return await emailService.sendEmployerAcceptedEmail(data.employerData);
        
      case 'EMPLOYER_REJECTED':
        return await emailService.sendEmployerRejectedEmail(data.employerData, data.reason);
        
      case 'STUDENT_APPLICATION_ACCEPTED':
        return await emailService.sendStudentApplicationAcceptedEmail(
          data.studentData, 
          data.positionData, 
          data.companyData
        );
        
      case 'STUDENT_APPLICATION_REJECTED':
        return await emailService.sendStudentApplicationRejectedEmail(
          data.studentData, 
          data.positionData, 
          data.companyData
        );
        
      case 'STUDENT_ACCEPTED_OFFER':
        return await emailService.sendStudentAcceptedOfferEmail(
          data.employerData, 
          data.studentData, 
          data.positionData
        );
        
      case 'STUDENT_DECLINED_OFFER':
        return await emailService.sendStudentDeclinedOfferEmail(
          data.employerData, 
          data.studentData, 
          data.positionData, 
          data.reason
        );
        
      default:
        throw new Error(`Unknown email notification type: ${type}`);
    }
  } catch (error) {
    console.error(`❌ Error sending ${type} email:`, error);
    // Don't throw error to prevent breaking the main functionality
    return { success: false, error: error.message };
  }
};